import React from 'react';
import { roundDownTotal } from '../screens/Consumables/consumableHelpers';

// ── Stamp / sign — adjust all positioning here ────────────────────────────
const STAMP_WIDTH        = 120;   // px — stamp image width
const STAMP_HEIGHT       = 120;   // px — stamp image height
const SIGN_WIDTH         = 130;  // px — signature image width
const SIGN_HEIGHT        = 130;  // px — signature image height
const IMG_GAP            = 6;    // px — horizontal gap between stamp and sign
const FOR_LHHS_MB        = -30;    // px — gap between "For LHHS" line and images
const IMG_MB             = -40;    // px — gap between images and "Authorised Signatory" line
const FOOTER_SECTION_MT  = 18;   // px — gap between invoice table and the whole footer row
// ──────────────────────────────────────────────────────────────────────────

// ---------- helpers ----------

const ones = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const tensArr = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function cvt100(n: number): string {
  if (n < 20) return ones[n];
  return tensArr[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
}
function cvt1000(n: number): string {
  if (n < 100) return cvt100(n);
  return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + cvt100(n % 100) : '');
}
export function amountToWords(amount: number): string {
  const n = Math.round(Math.abs(amount));
  if (n === 0) return 'Zero Rupees';
  const crore    = Math.floor(n / 10000000);
  const lakh     = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000)   / 1000);
  const rest     = n % 1000;
  let w = '';
  if (crore)    w += cvt1000(crore)    + ' Crore ';
  if (lakh)     w += cvt1000(lakh)     + ' Lakh ';
  if (thousand) w += cvt1000(thousand) + ' Thousand ';
  if (rest)     w += cvt1000(rest)     + ' ';
  return w.trim() + ' Rupees Only';
}

/** Format a date string (YYYY-MM-DD) → DD-MM-YYYY */
function dmyFmt(d: string): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}-${m}-${y}`;
}

/** Reformat a stored "YYYY-MM-DD to YYYY-MM-DD" detail string → "DD-MM-YYYY to DD-MM-YYYY" */
function fmtDetailDates(details: string): string {
  const match = details?.match(/^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/);
  if (match) return `${dmyFmt(match[1])} to ${dmyFmt(match[2])}`;
  return details || '';
}

/** Extract start date from item — prefers dedicated field, falls back to parsing details */
function itemStartDate(item: LineItem): string {
  if (item.start_date) return dmyFmt(item.start_date);
  const match = item.details?.match(/^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/);
  return match ? dmyFmt(match[1]) : (item.details || '');
}

/** Extract end date from item — prefers dedicated field, falls back to parsing details */
function itemEndDate(item: LineItem): string {
  if (item.end_date) return dmyFmt(item.end_date);
  const match = item.details?.match(/^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/);
  return match ? dmyFmt(match[2]) : '';
}

/** Format a date string (YYYY-MM-DD) → "28, Mar 2024" */
function longFmt(d: string): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Indian-locale number with 2 decimals */
function fmtNum(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------- interfaces ----------

interface LineItem {
  particulars: string;
  rate: number | '';
  details: string;
  shift_type: string;
  shifts_count: number | '';
  total_amount: number;
  is_miscellaneous: boolean;
  start_date?: string;
  end_date?: string;
  section_type?: string;
  has_gst?: boolean;
  gst_percent?: number;
  gst_amount?: number;
  mrp?: number | '';
  discount_percent?: number;
  disc_price?: number | '';
  quantity?: number | '';
  cgst_amount?: number;
  sgst_amount?: number;
  /** Medical equipment only: the HSN/SAC code, pre-filled from the device's
   *  registration when the equipment is picked and then editable per bill.
   *  Blank — as on bills raised before the column existed — prints as a dash. */
  hsn_code?: string;
}

interface ConsumableLineItem {
  particulars: string;
  hsn_sac: string;
  mrp: number | null;
  discount_rate: number | null;
  disc_price: number;
  quantity: number;
  gst_percent: number;
  cgst_amount: number;
  sgst_amount: number;
  subtotal: number;
}

interface Patient {
  id: number;
  patient_name: string;
  patient_salutation?: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { salutation?: string; client_name: string; contact_number_1: string; contact_number_2?: string; email: string }[];
}

interface Props {
  patient: Patient;
  billNo: number;
  /** Consumables only: the printed series, e.g. "LHHS/11154/26-27". When set it
   *  replaces the bare bill number on the invoice. */
  invoiceNo?: string | null;
  billDate: string;
  periodStart: string;
  periodEnd: string;
  lineItems: LineItem[];
  consumableItems?: ConsumableLineItem[];
  remarks: string;
  /** One concession off the whole bill. Omitted or zero prints nothing at all,
   *  so invoices without a discount look exactly as they always have. */
  discountDescription?: string | null;
  discountAmount?: number | null;
  billType?: string;
  hasGst?: boolean;
  createdAt?: string;
  // ── Multi-page consumables support (set by ConsumableInvoiceDocument) ──
  /** Offset added to the displayed item serial, so page 2 starts at 27, not 1. */
  itemStartNo?: number;
  /** Hide the grand-total row, remarks and signature — used on continuation
   *  pages so those appear only on the final page. */
  hideClosing?: boolean;
  // Whole-bill CGST/SGST, supplied on the last page of a paginated consumable
  // invoice. Without it that page would total only its own chunk of items, the
  // same reason consumableTotalOverride exists.
  consumableGstOverride?: { cgst: number; sgst: number };
  /** When paginated, each page only holds a slice of items; this carries the
   *  total across ALL pages so the last page shows the true grand total. */
  consumableTotalOverride?: number;
}

// ---------- component ----------

const BillInvoicePreview = React.forwardRef<HTMLDivElement, Props>(
  ({ patient, billNo, invoiceNo, billDate, periodStart, periodEnd, lineItems, consumableItems, remarks, discountDescription, discountAmount, billType, hasGst = true, createdAt, itemStartNo = 0, hideClosing = false, consumableTotalOverride, consumableGstOverride }, ref) => {
    const isMedEquip    = billType === 'medical_equipment' || billType === 'equipment_rental' || billType === 'equipment_sale';
    const isDoctorVisit = billType === 'doctor_visit';
    const isVisitType   = billType === 'nursing_visit' || billType === 'physio';
    const isMisc        = billType === 'misc';
    const isRateType    = billType === 'nursing' || billType === 'nursing_visit' || billType === 'physio';
    const isRefDoc      = isMedEquip && !hasGst;
    const isReturn       = billType === 'consumable_return';
    const isConsumables = billType === 'consumables' || isReturn;
    // total columns: doctor_visit=7, misc=6, others=8
    const totalCols     = isDoctorVisit ? 7 : isMisc ? 6 : 8;
    const client = patient.patient_clients?.[0];
    const contact = client?.contact_number_1 || '';
    const email = client?.email || '';

    const td0 = patient.treating_details?.[0];
    const allDoctors = patient.treating_details?.map(d => d.doctor).filter(Boolean) || [];
    const doctorName = allDoctors.length > 0 ? allDoctors : [patient.treating_doctor || ''];
    const hospitalName = td0?.hospital || patient.treating_hospital || '';

    const withSal = (sal?: string, name?: string) => [sal, name].filter(Boolean).join(' ');

    const allClients = patient.patient_clients?.filter(c => c.client_name) || [];
    const allContacts = patient.patient_clients?.flatMap(c =>
      [c.contact_number_1, c.contact_number_2].filter(Boolean)
    ) || [];

    const nursingItems       = lineItems.filter((i) => !i.is_miscellaneous);
    const miscItems          = lineItems.filter((i) => i.is_miscellaneous);
    const nursingTotal       = nursingItems.reduce((s, i) => s + i.total_amount, 0);
    const itemsTotal         = lineItems.reduce((s, i) => s + i.total_amount, 0);
    // Clamped to the bill value, matching the sheet — never a negative total.
    const discountValue      = Math.min(Math.max(Number(discountAmount) || 0, 0), itemsTotal);
    const hasDiscount        = discountValue > 0;
    const grandTotal         = itemsTotal - discountValue;
    // Consumables are payable in whole rupees — the paise are dropped in the
    // client's favour. Floored here rather than only in the entry screens so the
    // printed figure and the words below it can never disagree (amountToWords
    // rounds to nearest, which used to print 1397 against a figure of 1396.50).
    // The pre-floor sum is kept so the invoice can show Subtotal and Round Off
    // as their own rows instead of the net total silently disagreeing with the
    // line items above it.
    const consumableSubtotal = consumableTotalOverride != null
      ? consumableTotalOverride
      : (consumableItems ?? []).reduce((s, i) => s + i.subtotal, 0);
    // Summed from the same rows the table prints, so the totals row can never
    // disagree with the column above it. On a paginated bill the last page is
    // handed the whole-bill figure instead of its own chunk's.
    const consumableCgst = consumableGstOverride
      ? consumableGstOverride.cgst
      : (consumableItems ?? []).reduce((sum, i) => sum + (Number(i.cgst_amount) || 0), 0);
    const consumableSgst = consumableGstOverride
      ? consumableGstOverride.sgst
      : (consumableItems ?? []).reduce((sum, i) => sum + (Number(i.sgst_amount) || 0), 0);
    const consumableHasGst = consumableCgst > 0 || consumableSgst > 0;
    const consumableTotal    = roundDownTotal(consumableSubtotal);
    const consumableRoundOff = Math.round((consumableSubtotal - consumableTotal) * 100) / 100;
    const effectiveTotal     = isConsumables ? consumableTotal : grandTotal;

    // Medical equipment splits (by shift_type discriminator)
    const rentalItems  = isMedEquip ? lineItems.filter((i) => !i.is_miscellaneous && i.shift_type !== 'Sale') : [];
    const saleItems    = isMedEquip ? lineItems.filter((i) => !i.is_miscellaneous && i.shift_type === 'Sale') : [];
    const medMiscItems = isMedEquip ? lineItems.filter((i) => i.is_miscellaneous) : [];

    // Derived date range with fallback to first/last item dates (guards against empty period props)
    const displayStart = periodStart || rentalItems[0]?.start_date || '';
    const displayEnd   = periodEnd   || rentalItems[rentalItems.length - 1]?.end_date || '';

    // ---------- styles ----------
    const border = '1px solid #000';
    const thinBorder = '1px solid #000';
    const lightBorder = '1px solid #555';

    const fontFamily = '"Times New Roman", Times, serif';

    const thStyle: React.CSSProperties = {
      border: thinBorder, padding: '4px 6px', backgroundColor: '#e8e8e8',
      fontWeight: 700, fontSize: '10px', textAlign: 'center', fontFamily,
    };
    const tdC: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'center', color: '#000', fontFamily };
    const tdL: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'left', color: '#000', fontFamily };
    const tdR: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'right', color: '#000', fontFamily };

    const infoLabelStyle: React.CSSProperties = { fontWeight: 700, fontSize: '10px', padding: '3px 6px', borderRight: border, color: '#000', fontFamily };
    const infoValueStyle: React.CSSProperties = { fontSize: '10px', padding: '3px 6px', color: '#000', fontFamily };

    return (
      /*
       * A4 container: 794px × 1123px at 96dpi.
       * The letterhead.png is placed as a full-bleed background.
       * Invoice content is positioned with top/bottom padding to clear
       * the letterhead header (~165px) and footer (~130px).
       *
       * Place your letterhead image at: frontend/public/letterhead.png
       */
      <div
        ref={ref}
        style={{
          width: '794px',
          height: '1123px',
          position: 'relative',
          fontFamily: '"Times New Roman", Times, serif',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Letterhead background — hidden for medical equipment reference docs */}
        {!isRefDoc && (
          <img
            src="/letterhead.png"
            alt=""
            crossOrigin="anonymous"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill' }}
          />
        )}

        {/* Invoice content overlay */}
        <div
          style={{
            position: 'absolute',
            top: isRefDoc ? '28px' : '162px',
            left: '28px',
            right: '28px',
            bottom: isRefDoc ? '28px' : '128px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* ── Client info box (INVOICE header inside) ── */}
          <div style={{ border }}>

            {/* Top header row: INVOICE/REFERENCE | GSTIN */}
            <div style={{ display: 'flex', borderBottom: border }}>
              <div style={{ width: isRefDoc ? '100%' : '64%', borderRight: isRefDoc ? 'none' : border, padding: '5px 10px', textAlign: 'center', fontWeight: 800, fontSize: '14px', letterSpacing: '2px', fontFamily }}>
                {isRefDoc ? 'REFERENCE DOCUMENT' : isReturn ? 'RETURN BILL' : 'INVOICE'}
              </div>
              {!isRefDoc && (
                <div style={{ width: '40%', padding: '5px 10px', display: 'flex', alignItems: 'center', fontSize: '10px', fontWeight: 600, fontFamily }}>
                  GSTIN Number : 29AACCL5804A1ZV
                </div>
              )}
            </div>

            {/* Body: address | info grid */}
            <div style={{ display: 'flex' }}>
              {/* Left: To address */}
              <div style={{ width: '20%', borderRight: border, padding: '8px', fontSize: '10px', fontFamily }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>To,</div>
                <div style={{ fontWeight: 700 }}>{withSal(patient.patient_salutation, patient.patient_name)}</div>
                <div style={{ marginTop: '4px', lineHeight: 1.5, color: '#333' }}>
                  {patient.address || ''}
                </div>
              </div>

              {/* Right: bill info grid — natural column widths, no fixed constraints */}
              <div style={{ width: '80%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily }}>
                  <tbody>
                    {(() => {
                      const multiLine = (items: (string | number | undefined | null)[]) => (
                        <>
                          {items.filter(Boolean).map((item, idx, arr) => (
                            <React.Fragment key={idx}>
                              {item}{idx < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </>
                      );
                      const rows: [React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode][] = [
                        [
                          isRefDoc ? 'Reference No' : isReturn ? 'Return No' : invoiceNo ? 'Invoice No' : 'Bill No',
                          isReturn ? (invoiceNo || `R-${billNo}`) : (invoiceNo || billNo),
                          'Doctor', multiLine(doctorName),
                        ],
                        ['Date', dmyFmt(billDate), 'Hospital', hospitalName],
                        ['Patient', withSal(patient.patient_salutation, patient.patient_name), 'CID', `LHHS${patient.id}`],
                        ['Sex', patient.sex || '', 'Age', patient.age ? `${patient.age} Years` : ''],
                        ['Relative', multiLine(allClients.map(c => withSal(c.salutation, c.client_name))), 'Contact', multiLine(allContacts.length ? allContacts : [contact])],
                        ['Email :', email, 'Landline :', ''],
                      ];
                      return rows.map(([l1, v1, l2, v2], i) => {
                        const rowBorder = border;
                        return (
                          <tr key={i}>
                            <td style={{ ...infoLabelStyle, borderBottom: rowBorder, verticalAlign: 'top' }}>{l1}</td>
                            <td style={{ ...infoValueStyle, borderRight: border, borderBottom: rowBorder, wordBreak: 'break-word', whiteSpace: 'normal', fontWeight: i === 0 ? 800 : undefined, verticalAlign: 'top' }}>{v1}</td>
                            <td style={{ ...infoLabelStyle, borderBottom: rowBorder, verticalAlign: 'top' }}>{l2}</td>
                            <td style={{ ...infoValueStyle, borderBottom: rowBorder, wordBreak: 'break-word', whiteSpace: 'normal', verticalAlign: 'top' }}>{v2}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Case start date row — hidden for consumables and equipment sale invoices */}
            {!isConsumables && billType !== 'equipment_sale' && (
              <div style={{ borderTop: border, padding: '4px 8px', fontSize: '10px', fontFamily }}>
                <span style={{ fontWeight: 700 }}>Initial Case start Date : </span>
                {patient.case_start_date ? longFmt(patient.case_start_date) : ''}
              </div>
            )}
          </div>

          {/* ── Invoice details table ── */}
          <div style={{ border, marginTop: '-1px' }}>
            {/* Period header */}
            <div style={{ borderBottom: thinBorder, padding: '4px 8px', textAlign: 'center', fontWeight: 700, fontSize: '11px', backgroundColor: '#f9f9f9', fontFamily }}>
              {isReturn
                ? `Return Details (${dmyFmt(billDate)})`
                : isConsumables || billType === 'equipment_sale'
                  ? `Invoice Details (${dmyFmt(billDate)})`
                  : displayStart && displayEnd
                    ? `Invoice Details (${dmyFmt(displayStart)} to ${dmyFmt(displayEnd)})`
                    : displayStart || displayEnd
                      ? `Invoice Details (${dmyFmt(displayStart || displayEnd)})`
                      : 'Invoice Details'}
            </div>

            {isMedEquip ? (
              /* ── Medical Equipment — Rental + Sale tables ── */
              (() => {
                const rentalTotal = rentalItems.reduce((s, i) => s + i.total_amount, 0);
                const saleTotal   = saleItems.reduce((s, i) => s + i.total_amount, 0);
                const miscTotal   = medMiscItems.reduce((s, i) => s + i.total_amount, 0);
                const totalAmt    = rentalTotal + saleTotal + miscTotal;
                const subHead = (label: string, cols: number) => (
                  <tr>
                    <td colSpan={cols} style={{ border: thinBorder, padding: '3px 6px', backgroundColor: '#f5f3ff', fontWeight: 700, fontSize: '9px', fontFamily, color: '#7e22ce', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      {label}
                    </td>
                  </tr>
                );
                // Rental: 9 cols — No | Equipment | Start | End | Rate Type | Rate | GST% | GST Amt | Sub Total
                // Sale:   6 cols — No | Equipment | Selling Price | GST% | GST Amt | Sub Total
                return (
                  <>
                    {/* Rental Equipment — only rendered when rows exist */}
                    {rentalItems.length > 0 && (() => {
                      const rentalHasGst = rentalItems.some((i) => i.has_gst);
                      // +1 for the HSN column, which sits after End Date on both
                      // the GST and non-GST layouts.
                      const rentalCols = rentalHasGst ? 11 : 7;
                      // CGST and SGST are each half the GST rate. Every row on a
                      // bill normally carries the same rate (it comes from the
                      // device), so it goes in the header; a bill that mixes rates
                      // has no single figure, and the GST % column still shows it
                      // per row.
                      const rentalRates = Array.from(new Set(
                        rentalItems.filter(i => i.has_gst).map(i => Number(i.gst_percent) || 0).filter(p => p > 0)
                      ));
                      const rentalHalf = rentalRates.length === 1 ? rentalRates[0] / 2 : null;
                      const rentalHalfLabel = (base: string) =>
                        rentalHalf !== null ? `${base} (${+rentalHalf.toFixed(2)}%)` : base;
                      // Column sums for the Totals row below.
                      const rentalAmount = rentalItems.reduce((s, i) => s + (Number(i.rate) || 0), 0);
                      const rentalCgst = rentalItems.reduce((s, i) => s + (i.has_gst ? Number(i.cgst_amount) || 0 : 0), 0);
                      const rentalSgst = rentalItems.reduce((s, i) => s + (i.has_gst ? Number(i.sgst_amount) || 0 : 0), 0);
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <colgroup>
                            {/* HSN is paid for out of the money columns (Amount,
                                CGST, SGST, Total GST, Sub Total) — the Equipment
                                column keeps its full width, since a squeezed
                                device name wraps badly. */}
                            {rentalHasGst ? (
                              <>
                                <col style={{ width: '4%' }} />
                                <col style={{ width: '22%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '11%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '10%' }} />
                              </>
                            ) : (
                              <>
                                <col style={{ width: '4%' }} />
                                <col style={{ width: '32%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '13%' }} />
                              </>
                            )}
                          </colgroup>
                          <thead>
                            {subHead('Medical Equipment Rental Services', rentalCols)}
                            <tr>
                              <th style={{ ...thStyle, textAlign: 'center' }}>No.</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Equipment</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Start Date</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>End Date</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>HSN</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Amount</th>
                              {rentalHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>GST %</th>}
                              {rentalHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>{rentalHalfLabel('CGST')}</th>}
                              {rentalHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>{rentalHalfLabel('SGST')}</th>}
                              {rentalHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>Total GST</th>}
                              <th style={{ ...thStyle, textAlign: 'center' }}>Sub Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rentalItems.map((item, i) => (
                              <tr key={i}>
                                <td style={tdC}>{i + 1}</td>
                                <td style={tdC}>{item.particulars}</td>
                                <td style={tdC}>{item.start_date ? dmyFmt(item.start_date) : '—'}</td>
                                <td style={tdC}>{item.end_date ? dmyFmt(item.end_date) : '—'}</td>
                                <td style={tdC}>{item.hsn_code || '—'}</td>
                                <td style={tdC}>{item.rate !== '' ? fmtNum(Number(item.rate)) : '—'}</td>
                                {rentalHasGst && <td style={tdC}>{item.has_gst && item.gst_percent ? `${item.gst_percent}%` : '—'}</td>}
                                {rentalHasGst && <td style={tdC}>{item.has_gst && item.cgst_amount ? fmtNum(item.cgst_amount) : '—'}</td>}
                                {rentalHasGst && <td style={tdC}>{item.has_gst && item.sgst_amount ? fmtNum(item.sgst_amount) : '—'}</td>}
                                {rentalHasGst && <td style={tdC}>{item.has_gst && (item.cgst_amount || item.sgst_amount) ? fmtNum((item.cgst_amount || 0) + (item.sgst_amount || 0)) : '—'}</td>}
                                <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(item.total_amount)}</td>
                              </tr>
                            ))}
                            {/* Totals row — each money column sums itself under its
                                own heading (Amount, CGST, SGST, GST, Sub Total), so
                                those figures need no labels of their own. The label
                                spans the first five (No/Equipment/Start/End/HSN)
                                columns — HSN has nothing to total either. */}
                            <tr style={{ backgroundColor: '#faf5ff' }}>
                              <td colSpan={5} style={{ ...tdC, fontWeight: 700 }}>Sub Total</td>
                              <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(rentalAmount)}</td>
                              {/* GST % stays a column but has no total — a rate doesn't sum. */}
                              {rentalHasGst && <td style={tdC} />}
                              {rentalHasGst && <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(rentalCgst)}</td>}
                              {rentalHasGst && <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(rentalSgst)}</td>}
                              {rentalHasGst && <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(rentalCgst + rentalSgst)}</td>}
                              <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(rentalTotal)}</td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}

                    {/* Sale Equipment — only rendered when rows exist */}
                    {saleItems.length > 0 && (() => {
                      const saleHasGst = saleItems.some((i) => i.has_gst);
                      // +1 for HSN. A sale row has no date range, so the column
                      // follows the equipment name instead.
                      const saleCols = saleHasGst ? 11 : 8;
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '2px' }}>
                          <colgroup>
                            {/* As on the rental table, HSN comes out of the money
                                columns and the Equipment name keeps its width. */}
                            {saleHasGst ? (
                              <>
                                <col style={{ width: '3%' }} />
                                <col style={{ width: '26%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '5%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '14%' }} />
                              </>
                            ) : (
                              <>
                                <col style={{ width: '4%' }} />
                                <col style={{ width: '34%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '16%' }} />
                              </>
                            )}
                          </colgroup>
                          <thead>
                            {subHead('Sale Equipment', saleCols)}
                            <tr>
                              <th style={{ ...thStyle, textAlign: 'center' }}>No.</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Equipment</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>HSN</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>MRP (INR)</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Disc %</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Disc Price (INR)</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Qty</th>
                              {saleHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>GST %</th>}
                              {saleHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>CGST</th>}
                              {saleHasGst && <th style={{ ...thStyle, textAlign: 'center' }}>SGST</th>}
                              <th style={{ ...thStyle, textAlign: 'center' }}>Sub Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {saleItems.map((item, i) => (
                              <tr key={i}>
                                <td style={tdC}>{i + 1}</td>
                                <td style={tdC}>{item.particulars}</td>
                                <td style={tdC}>{item.hsn_code || '—'}</td>
                                <td style={tdC}>{item.mrp !== '' && item.mrp != null ? fmtNum(Number(item.mrp)) : '—'}</td>
                                <td style={tdC}>{item.discount_percent != null ? `${Number(item.discount_percent).toFixed(2)}%` : '—'}</td>
                                <td style={tdC}>{item.disc_price !== '' && item.disc_price != null ? fmtNum(Number(item.disc_price)) : '—'}</td>
                                <td style={tdC}>{item.quantity !== '' && item.quantity != null ? item.quantity : '—'}</td>
                                {saleHasGst && <td style={tdC}>{item.has_gst && item.gst_percent ? `${item.gst_percent}%` : '—'}</td>}
                                {saleHasGst && <td style={tdC}>{item.has_gst && item.cgst_amount ? fmtNum(item.cgst_amount) : '—'}</td>}
                                {saleHasGst && <td style={tdC}>{item.has_gst && item.sgst_amount ? fmtNum(item.sgst_amount) : '—'}</td>}
                                <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(item.total_amount)}</td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#faf5ff' }}>
                              <td colSpan={saleCols - 1} style={{ ...tdC, fontWeight: 700 }}>Sale Sub-Total</td>
                              <td style={{ ...tdC, fontWeight: 700 }}>{fmtNum(saleTotal)}</td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}

                    {/* Misc rows */}
                    {medMiscItems.length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: '2px' }}>
                        <colgroup>
                          <col style={{ width: '4%' }} />
                          <col style={{ width: '80%' }} />
                          <col style={{ width: '16%' }} />
                        </colgroup>
                        <thead>
                          {subHead('Miscellaneous', 3)}
                          <tr>
                            <th style={{ ...thStyle, textAlign: 'center' }}>No.</th>
                            <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {medMiscItems.map((item, i) => (
                            <tr key={i}>
                              <td style={tdC}>{i + 1}</td>
                              <td style={tdL}>{item.particulars}</td>
                              <td style={tdR}>{fmtNum(item.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Grand Total. A discount was captured and saved on these
                        bills but never applied here — the payable line was built
                        from the item totals alone, so equipment invoices printed
                        (and were paid at) the undiscounted amount. */}
                    {(() => {
                      const equipDiscount = Math.min(Math.max(Number(discountAmount) || 0, 0), totalAmt);
                      const equipPayable  = totalAmt - equipDiscount;
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {/* No Sub Total row here — the Totals row in the table
                                above already states it, under its own column. */}
                            {equipDiscount > 0 && (
                              <tr style={{ backgroundColor: '#ffffff' }}>
                                <td style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily, width: '84%' }}>
                                  {(discountDescription || '').trim() || 'Discount'}
                                </td>
                                <td style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily, width: '16%' }}>
                                  − {fmtNum(equipDiscount)}
                                </td>
                              </tr>
                            )}
                            <tr style={{ backgroundColor: '#ffffff' }}>
                              <td style={{ border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 700, borderTop: '2px solid #149bc1', textAlign: 'center', fontFamily, color: '#149bc1', width: '84%' }}>
                                Net Total Payable Amount ({amountToWords(equipPayable)})
                              </td>
                              <td style={{ border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 800, textAlign: 'right', borderTop: '2px solid #149bc1', fontFamily, color: '#149bc1', width: '16%' }}>
                                INR {fmtNum(equipPayable)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  </>
                );
              })()
            ) : isConsumables ? (
              /* ── Consumables GST table ── */
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '3%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thStyle}>No.</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Particulars</th>
                    <th style={thStyle}>HSN/SAC</th>
                    <th style={thStyle}>MRP (INR)</th>
                    <th style={thStyle}>Disc. %</th>
                    <th style={thStyle}>{isReturn ? 'Return Price (INR)' : 'Disc. Price (INR)'}</th>
                    <th style={thStyle}>{isReturn ? 'Qty Returned' : 'Qty'}</th>
                    <th style={thStyle}>GST %</th>
                    <th style={thStyle}>CGST</th>
                    <th style={thStyle}>SGST</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Sub Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(consumableItems ?? []).map((item, i) => (
                    <tr key={i}>
                      <td style={tdC}>{itemStartNo + i + 1}</td>
                      <td style={tdL}>{item.particulars}</td>
                      <td style={tdC}>{item.hsn_sac || '—'}</td>
                      <td style={tdR}>{item.mrp != null ? fmtNum(item.mrp) : '—'}</td>
                      <td style={tdC}>{item.discount_rate != null ? `${fmtNum(item.discount_rate)}%` : '—'}</td>
                      <td style={tdR}>{fmtNum(item.disc_price)}</td>
                      <td style={tdC}>{item.quantity}</td>
                      <td style={tdC}>{item.gst_percent > 0 ? `${item.gst_percent}%` : '—'}</td>
                      <td style={tdR}>{item.cgst_amount > 0 ? fmtNum(item.cgst_amount) : '—'}</td>
                      <td style={tdR}>{item.sgst_amount > 0 ? fmtNum(item.sgst_amount) : '—'}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(item.subtotal)}</td>
                    </tr>
                  ))}

                  {/* Subtotal + round off — last page only, and only when the
                      floor actually dropped something. Shown so the printed net
                      total visibly follows from the line items above rather than
                      appearing a few paise short of their sum. */}
                  {/* Sub Total prints whenever there is GST to total, not only
                      when the floor dropped paise — the CGST/SGST columns need a
                      total of their own regardless. The label now spans only the
                      first eight columns so CGST and SGST can carry their own
                      figures, the way the Equipment Rental table already does. */}
                  {!hideClosing && (consumableRoundOff > 0 || consumableHasGst) && (
                    <>
                      <tr>
                        <td
                          colSpan={consumableHasGst ? 8 : 10}
                          style={{
                            border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 600,
                            textAlign: 'right', fontFamily, color: '#000',
                          }}
                        >
                          Sub Total
                        </td>
                        {consumableHasGst && (
                          <td
                            style={{
                              border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 700,
                              textAlign: 'right', fontFamily, color: '#000',
                            }}
                          >
                            {fmtNum(consumableCgst)}
                          </td>
                        )}
                        {consumableHasGst && (
                          <td
                            style={{
                              border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 700,
                              textAlign: 'right', fontFamily, color: '#000',
                            }}
                          >
                            {fmtNum(consumableSgst)}
                          </td>
                        )}
                        <td
                          style={{
                            border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 700,
                            textAlign: 'right', fontFamily, color: '#000',
                          }}
                        >
                          {fmtNum(consumableSubtotal)}
                        </td>
                      </tr>
                      {consumableRoundOff > 0 && (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 600,
                            textAlign: 'right', fontFamily, color: '#000',
                          }}
                        >
                          Round Off
                        </td>
                        <td
                          style={{
                            border: thinBorder, padding: '4px 8px', fontSize: '9px', fontWeight: 700,
                            textAlign: 'right', fontFamily, color: '#000',
                          }}
                        >
                          − {fmtNum(consumableRoundOff)}
                        </td>
                      </tr>
                      )}
                    </>
                  )}

                  {/* Grand total — last page only when paginated */}
                  {!hideClosing && (
                  <tr style={{ backgroundColor: '#ffffff' }}>
                    <td
                      colSpan={10}
                      style={{
                        border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 700,
                        borderTop: '2px solid #149bc1', textAlign: 'center', fontFamily,
                        color: '#149bc1',
                      }}
                    >
                      {isReturn ? 'Net Credit Amount' : 'Net Total Payable Amount'} ({amountToWords(consumableTotal)})
                    </td>
                    <td
                      style={{
                        border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 800,
                        textAlign: 'right', borderTop: '2px solid #149bc1', fontFamily,
                        color: '#149bc1',
                      }}
                    >
                      INR {fmtNum(consumableTotal)}
                    </td>
                  </tr>
                  )}
                </tbody>
              </table>
            ) : (
              /* ── Standard table (all non-consumable bill types) ── */
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: '4%' }}>S.No</th>
                    <th style={{ ...thStyle, width: isDoctorVisit ? '20%' : '18%', textAlign: 'left' }}>Particulars</th>
                    {isDoctorVisit && <th style={{ ...thStyle, width: '18%' }}>Doctor Name</th>}
                    <th style={{ ...thStyle, width: '12%' }}>Start Date</th>
                    <th style={{ ...thStyle, width: '12%' }}>End Date</th>
                    {!isDoctorVisit && !isMisc && <th style={{ ...thStyle, width: '16%' }}>{isVisitType ? 'Visit Type' : 'Shift Type'}</th>}
                    <th style={{ ...thStyle, width: '8%' }}>{isRateType ? 'Rate' : 'Amount'}</th>
                    {!isDoctorVisit && !isMisc && <th style={{ ...thStyle, width: '8%' }}>{isVisitType ? 'Visits' : 'Shifts'}</th>}
                    <th style={{ ...thStyle, width: isDoctorVisit ? '26%' : '18%', textAlign: 'right' }}>Sub Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Charge rows */}
                  {nursingItems.map((item, i) => (
                    <tr key={i}>
                      <td style={tdC}>{i + 1}</td>
                      <td style={tdL}>{item.particulars}</td>
                      {isDoctorVisit && <td style={tdC}>{item.shift_type}</td>}
                      <td style={tdC}>{itemStartDate(item)}</td>
                      <td style={tdC}>{itemEndDate(item)}</td>
                      {!isDoctorVisit && !isMisc && <td style={tdC}>{item.shift_type}</td>}
                      <td style={tdC}>{item.rate !== '' ? Number(item.rate).toLocaleString('en-IN') : ''}</td>
                      {!isDoctorVisit && !isMisc && <td style={tdC}>{item.shifts_count !== '' ? item.shifts_count : ''}</td>}
                      <td style={tdR}>{fmtNum(item.total_amount)}</td>
                    </tr>
                  ))}

                  {/* Subtotal row */}
                  {nursingItems.length > 0 && (
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <td colSpan={totalCols - 1} style={{ ...tdC, fontWeight: 700 }}>
                        Total ({dmyFmt(periodStart)} to {dmyFmt(periodEnd)})
                      </td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(nursingTotal)}</td>
                    </tr>
                  )}

                  {/* Miscellaneous rows */}
                  {miscItems.map((item, i) => (
                    <tr key={`misc-${i}`}>
                      <td style={tdC}>{nursingItems.length + i + 1}</td>
                      <td colSpan={isDoctorVisit ? 3 : 4} style={tdL}>{item.particulars}</td>
                      <td style={tdC}>{itemStartDate(item)}</td>
                      <td style={tdC}>{itemEndDate(item)}</td>
                      <td style={tdR}>{fmtNum(item.total_amount)}</td>
                    </tr>
                  ))}

                  {/* Grand total */}
                  {/* Discount, printed only when there is one. Shows the gross
                      first so the client can see what was taken off. */}
                  {hasDiscount && (
                    <>
                      <tr style={{ backgroundColor: '#ffffff' }}>
                        <td colSpan={totalCols - 1} style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily }}>
                          Total
                        </td>
                        <td style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily }}>
                          {fmtNum(itemsTotal)}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#ffffff' }}>
                        <td colSpan={totalCols - 1} style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily }}>
                          {(discountDescription || '').trim() || 'Discount'}
                        </td>
                        <td style={{ border: thinBorder, padding: '4px 8px', fontSize: '10px', textAlign: 'right', fontFamily }}>
                          − {fmtNum(discountValue)}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr style={{ backgroundColor: '#ffffff' }}>
                    <td
                      colSpan={totalCols - 1}
                      style={{
                        border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 700,
                        borderTop: '2px solid #149bc1', textAlign: 'center', fontFamily,
                        color: '#149bc1',
                      }}
                    >
                      Net Total Payable Amount ({amountToWords(grandTotal)})
                    </td>
                    <td
                      style={{
                        border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 800,
                        textAlign: 'right', borderTop: '2px solid #149bc1', fontFamily,
                        color: '#149bc1',
                      }}
                    >
                      INR {fmtNum(grandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* Remarks — last page only when paginated */}
            {!hideClosing && (
            <div style={{ borderTop: thinBorder, padding: '4px 8px', fontSize: '10px', minHeight: '28px', fontFamily }}>
              <span style={{ fontWeight: 700 }}>Remarks</span>
              {remarks && <span style={{ marginLeft: '8px' }}>{remarks}</span>}
            </div>
            )}

            {/* GST summary — every line on the bill rolled up, so the tax total
                can be read off without adding the column by hand. Only shown
                when something on the bill actually carries GST. */}
            {/* GST is totalled in the Totals row of each table, under the
                columns it belongs to — a second summary here repeated it. */}
          </div>

          {/* ── Declaration / Signature — last page only when paginated ── */}
          {!hideClosing && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: `${FOOTER_SECTION_MT}px`, fontSize: '9px', fontFamily }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: '1px solid #555', marginTop: '30px', paddingTop: '4px', width: '120px', fontFamily }}>
                Customer Signature
              </div>
            </div>

            <div style={{ textAlign: 'justify', maxWidth: '260px', lineHeight: 1.6, color: '#333', fontFamily }}>
              <div style={{ fontWeight: 700, marginBottom: '4px', textAlign: 'center' }}>Declaration</div>
              <div>
                {isReturn
                  ? 'We hereby certify that the items returned and the credit mentioned herein are true, accurate, and in accordance with the agreed terms. This document is computer-generated and is valid without a physical signature or stamp.'
                  : 'We hereby certify that the goods & services rendered and the charges mentioned herein are true, accurate, and in accordance with the agreed terms. This invoice is computer-generated and is valid without a physical signature or stamp.'}
              </div>
            </div>

            <div style={{ textAlign: 'right', fontFamily }}>
              <div style={{ fontWeight: 700, marginBottom: `${FOR_LHHS_MB}px` }}>For LHHS Healthcare Pvt. Ltd.</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: `${IMG_GAP}px`, marginBottom: `${IMG_MB}px` }}>
                <img
                  crossOrigin="anonymous"
                  src="/assets/stamp.png"
                  alt="stamp"
                  style={{ width: STAMP_WIDTH, height: STAMP_HEIGHT, objectFit: 'contain', mixBlendMode: 'multiply' }}
                />
                <img
                  crossOrigin="anonymous"
                  src="/assets/sign.png"
                  alt="signature"
                  style={{ width: SIGN_WIDTH, height: SIGN_HEIGHT, objectFit: 'contain', mixBlendMode: 'multiply' }}
                />
              </div>
              <div style={{ borderTop: '1px solid #555', paddingTop: '4px', width: '130px', marginLeft: 'auto' }}>
                Authorised Signatory
                {createdAt && (() => {
                  const d = new Date(createdAt);
                  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
                  return (
                    <div style={{ fontSize: '7.5px', color: '#555', marginTop: '3px' }}>
                      Generated: {date}, {time}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }
);

BillInvoicePreview.displayName = 'BillInvoicePreview';


// ─────────────────────────────────────────────────────────────────────────────
// Multi-page wrapper for consumables invoices.
// A single invoice page is fixed-height with overflow hidden, so a long item list
// gets clipped. This splits consumables into PAGE_SIZE-per-page chunks and renders
// one full invoice page each (header repeats on every page, per the chosen layout);
// the grand total + signature land on the final page only. For ≤ PAGE_SIZE items
// (or any non-consumable bill) it renders exactly one page, unchanged.
//
// Each page is wrapped in a [data-invoice-page] div so downloadInvoicePDF can emit
// one PDF page per element.
// ─────────────────────────────────────────────────────────────────────────────
export const CONSUMABLE_ITEMS_PER_PAGE = 26;

export const ConsumableInvoiceDocument = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const items = props.consumableItems ?? [];
    const isConsumables = props.billType === 'consumables' || props.billType === 'consumable_return';

    if (!isConsumables || items.length <= CONSUMABLE_ITEMS_PER_PAGE) {
      // Single page — behaves exactly like before.
      return <BillInvoicePreview {...props} ref={ref} />;
    }

    const pages: ConsumableLineItem[][] = [];
    for (let i = 0; i < items.length; i += CONSUMABLE_ITEMS_PER_PAGE) {
      pages.push(items.slice(i, i + CONSUMABLE_ITEMS_PER_PAGE));
    }
    const grandTotal = items.reduce((s, it) => s + it.subtotal, 0);
    // Whole-bill GST for the closing page: each page only receives its own
    // chunk of items, so without this the totals row would report that page's
    // GST as if it were the bill's.
    const grandCgst = items.reduce((s, it) => s + (Number(it.cgst_amount) || 0), 0);
    const grandSgst = items.reduce((s, it) => s + (Number(it.sgst_amount) || 0), 0);

    return (
      <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {pages.map((chunk, pi) => {
          const isLast = pi === pages.length - 1;
          return (
            <div data-invoice-page key={pi}>
              <BillInvoicePreview
                {...props}
                consumableItems={chunk}
                itemStartNo={pi * CONSUMABLE_ITEMS_PER_PAGE}
                hideClosing={!isLast}
                consumableTotalOverride={isLast ? grandTotal : undefined}
                consumableGstOverride={isLast ? { cgst: grandCgst, sgst: grandSgst } : undefined}
              />
            </div>
          );
        })}
      </div>
    );
  }
);

ConsumableInvoiceDocument.displayName = 'ConsumableInvoiceDocument';

export default BillInvoicePreview;
